/**
 * Cómo autorizarnos en la Sede — `OD-0039` E-10 (2026-09-28).
 *
 * Página propia, con URL estable (`/como-autorizar`), que se abre en **otra
 * pestaña** desde la puerta del alta. Ahí está el trámite entero, con los datos
 * que hay que teclear y dibujos de las dos pantallas donde la gente se pierde.
 *
 * ## Por qué dibujos y no capturas de la Sede
 *
 * Una captura de la AEAT es contenido suyo, y además **caduca**: en cuanto
 * cambien un botón, la captura miente y nadie se entera. Los dibujos son
 * nuestros, muestran sólo la estructura —qué campo, qué columna, qué casilla—
 * y se corrigen en un minuto.
 *
 * ## El trámite se identifica por su DESCRIPCIÓN, no por su código
 *
 * Los códigos (`IZ860`, `IZ862`…) son de la AEAT y pueden cambiar sin avisar.
 * Lo que no cambia es lo que dicen. Por eso el paso manda mirar la columna
 * «Descripción» y buscar **remisión de registros de facturación por servicio
 * web**, y el código va sólo como pista.
 *
 * Y se avisa de lo contrario, que es el error caro: los trámites de la
 * *aplicación de facturación de la AEAT* (`IZ862`, `IZ863`) **no** sirven para
 * esto. Apoderar sólo esos deja un poder que parece bueno y con el que todas
 * las facturas fallarían al remitirse.
 */
import { useState } from "react";
import { api, type ErrorApi } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Tarjeta } from "../ui/primitivas";

/** El apoderado somos nosotros (`TDS-CORE` Parte 4 §1). */
const APODERADO = { nombre: "JOMINICOL SPAIN SL", nif: "B37582483" } as const;

/**
 * La primera pantalla: los datos del poder. Dibujo, no captura: enseña qué
 * campo es cada cual, que es lo que hace falta para no equivocarse.
 */
function DibujoDatos() {
  const c = t.comoAutorizar.campos;
  return (
    <svg className="dibujo-sede" viewBox="0 0 520 190" role="img" aria-label={t.comoAutorizar.dibujoDatosAlt}>
      <rect x="1" y="1" width="518" height="188" rx="6" className="dibujo-marco" />
      <text x="20" y="30" className="dibujo-etiqueta">{c.nifApoderado}</text>
      <rect x="20" y="40" width="240" height="26" rx="4" className="dibujo-campo dibujo-campo-clave" />
      <text x="30" y="58" className="dibujo-valor">{APODERADO.nif}</text>

      <text x="300" y="30" className="dibujo-etiqueta">{c.fechaFin}</text>
      <rect x="300" y="40" width="200" height="26" rx="4" className="dibujo-campo" />
      <text x="310" y="58" className="dibujo-valor">{t.comoAutorizar.fechaEjemplo}</text>

      <text x="20" y="100" className="dibujo-etiqueta">{c.absorbente}</text>
      <circle cx="28" cy="118" r="7" className="dibujo-radio dibujo-radio-marcado" />
      <text x="42" y="123" className="dibujo-valor">{c.no}</text>
      <circle cx="90" cy="118" r="7" className="dibujo-radio" />
      <text x="104" y="123" className="dibujo-valor">{c.si}</text>

      <text x="20" y="160" className="dibujo-nota">{t.comoAutorizar.dibujoDatosNota}</text>
    </svg>
  );
}

/**
 * La segunda: la búsqueda de trámites. Es donde se equivoca todo el mundo, así
 * que el dibujo marca la fila que hay que señalar y tacha las que no.
 */
function DibujoTramites() {
  const filas = t.comoAutorizar.tramites;
  const c = t.comoAutorizar.campos;
  return (
    <svg className="dibujo-sede" viewBox="0 0 520 250" role="img" aria-label={t.comoAutorizar.dibujoTramitesAlt}>
      <rect x="1" y="1" width="518" height="248" rx="6" className="dibujo-marco" />
      <text x="20" y="28" className="dibujo-etiqueta">{c.busqueda}</text>
      <rect x="20" y="38" width="300" height="26" rx="4" className="dibujo-campo dibujo-campo-clave" />
      <text x="30" y="56" className="dibujo-valor">{c.terminoBusqueda}</text>
      <rect x="336" y="38" width="76" height="26" rx="4" className="dibujo-boton" />
      <text x="356" y="56" className="dibujo-valor">{c.buscar}</text>

      {/* El boton que FIJA lo marcado. Sin pulsarlo se pierden las casillas, y
          es donde mas gente se queda sin enterarse de que no ha hecho nada. */}
      <rect x="20" y="72" width="80" height="24" rx="4" className="dibujo-boton dibujo-boton-clave" />
      <text x="38" y="89" className="dibujo-valor">{t.comoAutorizar.aceptar}</text>

      {filas.map((f, i) => {
        const y = 126 + i * 26;
        return (
          <g key={f.codigo}>
            <rect
              x="20"
              y={y - 14}
              width="480"
              height="24"
              rx="4"
              className={f.marcar ? "dibujo-fila dibujo-fila-marcada" : "dibujo-fila"}
            />
            <rect x="30" y={y - 8} width="12" height="12" rx="3" className={f.marcar ? "dibujo-casilla dibujo-casilla-marcada" : "dibujo-casilla"} />
            {f.marcar ? <path d="M33 -2 l3 4 l6 -7" transform={`translate(0 ${y})`} className="dibujo-tic" /> : null}
            <text x="56" y={y + 4} className="dibujo-codigo">{f.codigo}</text>
            <text x="110" y={y + 4} className="dibujo-descripcion">{f.texto}</text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Lo que se puede decir después de firmar en la Sede. Ni una palabra de esto
 * ACTIVA la autorización: declarar es pedir que lo comprobemos, y quien lo
 * comprueba somos nosotros contra la Sede (`OD-0031` D-03).
 */
type Resultado =
  | "nada"
  | "enviando"
  | "hecho"
  | "ya-anotada"
  | "ya-activa"
  | "faltan-datos"
  | "sin-sesion"
  | "solo-propietario"
  | "sin-red"
  | "no-se-pudo";

/** Estados desde los que hay que ELEGIR la ruta antes de poder declarar. */
const SIN_EMPEZAR = ["NOT_STARTED", "REJECTED", "REVOKED", "NIF_CHANGED"];

export function PantallaComoAutorizar() {
  const [resultado, setResultado] = useState<Resultado>("nada");
  /**
   * Lo que la API dijo, cuando lo dijo. Sus mensajes están escritos para
   * leerse («Todavía no has elegido cómo autorizarnos»), así que tragárselos
   * y enseñar un genérico es perder la única explicación que había.
   */
  const [explicacion, setExplicacion] = useState<string | null>(null);

  /**
   * El botón está AQUÍ, al final de la guía, porque aquí es donde se acaba de
   * firmar: mandar a la persona a buscar el mismo botón en otra pestaña es
   * perder por el camino a quien ya ha hecho lo difícil.
   *
   * Esta pantalla es pública —se abre en una pestaña aparte, y puede abrirse
   * sin sesión—, así que el caso de "no hay sesión" no es un error raro: es
   * uno de los normales, y se contesta diciendo dónde está el mismo botón.
   *
   * ## Por qué se PREGUNTA antes de declarar
   *
   * A esta guía se llega también sin pasar por la puerta: por el enlace del
   * correo, por un marcador o en una pestaña que llevaba días abierta. En ese
   * caso la ruta todavía no está elegida y declarar da un conflicto —lo que le
   * pasó a la primera persona que lo probó en staging—. Así que primero se
   * mira en qué punto está y se hace lo que falte:
   *
   * ```text
   * sin empezar          se elige la ruta 1 (la del tramite que acaba de
   *                      hacer) y se declara, igual que hace la puerta
   * esperando firma      se declara
   * ya en comprobacion   no se toca nada: se dice que ya consta
   * ya activa            no se toca nada: no hay nada que hacer
   * ```
   *
   * Elegir la ruta aquí no decide nada por nadie: es la ruta del trámite que
   * la persona acaba de hacer y está diciendo que hizo.
   */
  async function declarar(): Promise<void> {
    setResultado("enviando");
    setExplicacion(null);

    const actual = await api.autorizacionAeat();
    if (!actual.ok) return setResultado(fallo(actual.estado, actual.error));

    const estado = actual.datos.estado;
    if (estado === "ACTIVE") return setResultado("ya-activa");
    if (estado === "PENDING_VALIDATION") return setResultado("ya-anotada");

    if (SIN_EMPEZAR.includes(estado)) {
      const elegida = await api.elegirAutorizacion("APODERAMIENTO");
      if (!elegida.ok) return setResultado(fallo(elegida.estado, elegida.error));
    }

    const r = await api.declararAutorizacion();
    if (r.ok) return setResultado("hecho");
    setResultado(fallo(r.estado, r.error));
  }

  /** Un fallo, traducido a algo que diga QUÉ hacer. */
  function fallo(estado: number, error: ErrorApi | null): Resultado {
    if (estado === 401) return "sin-sesion";
    if (estado === 403) return "solo-propietario";
    if (estado === 0) return "sin-red";
    // Sin razón social, NIF y domicilio no se puede autorizar: es el paso 3 de
    // la puerta, y decirlo es más útil que un error genérico.
    if (error?.motivo === "LEGAL_ENTITY_MISSING") return "faltan-datos";
    // Lo demás: se enseña lo que la API explique, y sólo si no explica nada
    // se cae al genérico.
    const dicho = [error?.message ?? "", error?.action ?? ""].filter((x) => x !== "").join(" ");
    setExplicacion(dicho === "" ? null : dicho);
    return "no-se-pudo";
  }

  return (
    <Tarjeta titulo={t.comoAutorizar.titulo}>
      {/* Todo el cuerpo en un bloque propio: la guia manda en su ancho
          de linea y en su ritmo, que no son los de un formulario. */}
      <div className="guia">
        <p className="entradilla">{t.comoAutorizar.entradilla}</p>

        <Aviso tono="info" titulo={t.comoAutorizar.datosTitulo}>
          <>
            <strong>{APODERADO.nombre}</strong>
            {" — "}
            {t.comoAutorizar.campos.nif} <strong>{APODERADO.nif}</strong>
          </>
        </Aviso>

        <h3>{t.comoAutorizar.paso1Titulo}</h3>
        <p>{t.comoAutorizar.paso1}</p>
        <p>
          <a className="enlace-como-boton" href={t.autorizacion.enlaceSede} target="_blank" rel="noopener noreferrer">
            {t.comoAutorizar.abrirSede}
          </a>
        </p>

        <h3>{t.comoAutorizar.paso2Titulo}</h3>
        <p>{t.comoAutorizar.paso2}</p>

        <h3>{t.comoAutorizar.paso3Titulo}</h3>
        <p>{t.comoAutorizar.paso3}</p>
        <DibujoDatos />

        <h3>{t.comoAutorizar.paso4Titulo}</h3>
        <p>{t.comoAutorizar.paso4}</p>
        <p>{t.comoAutorizar.paso4Aceptar}</p>
        <DibujoTramites />
        <Aviso tono="aviso" titulo={t.comoAutorizar.ojoTitulo}>
          {t.comoAutorizar.ojoDetalle}
        </Aviso>

        <h3>{t.comoAutorizar.paso5Titulo}</h3>
        <p>{t.comoAutorizar.paso5}</p>

        <h3>{t.comoAutorizar.despuesTitulo}</h3>
        <p>{t.comoAutorizar.despues}</p>

        {resultado === "hecho" || resultado === "ya-anotada" || resultado === "ya-activa" ? (
          resultado === "hecho" ? (
            <Aviso tono="exito" titulo={t.comoAutorizar.hechoTitulo}>
              {t.comoAutorizar.hechoDetalle}
            </Aviso>
          ) : (
            <p>
              <a className="enlace-como-boton" href="/">
                {t.comoAutorizar.volver}
              </a>
            </p>
          )
        ) : (
          <div className="acciones">
            <Boton onClick={() => void declarar()} deshabilitado={resultado === "enviando"}>
              {t.autorizacion.yaLoHeHecho}
            </Boton>
            <a className="enlace-como-boton" href="/">
              {t.comoAutorizar.volver}
            </a>
          </div>
        )}

        {resultado === "ya-anotada" ? (
          <Aviso tono="info" titulo={t.comoAutorizar.yaAnotadaTitulo}>
            {t.comoAutorizar.yaAnotadaDetalle}
          </Aviso>
        ) : null}
        {resultado === "ya-activa" ? (
          <Aviso tono="exito" titulo={t.comoAutorizar.yaActivaTitulo}>
            {t.comoAutorizar.yaActivaDetalle}
          </Aviso>
        ) : null}
        {resultado === "faltan-datos" ? (
          <Aviso tono="aviso" titulo={t.comoAutorizar.faltanDatosTitulo}>
            {t.comoAutorizar.faltanDatosDetalle}
          </Aviso>
        ) : null}
        {resultado === "sin-sesion" ? (
          <Aviso tono="info" titulo={t.comoAutorizar.sinSesionTitulo}>
            {t.comoAutorizar.sinSesionDetalle}
          </Aviso>
        ) : null}
        {resultado === "solo-propietario" ? <Aviso tono="error">{t.autorizacion.soloPropietario}</Aviso> : null}
        {resultado === "sin-red" ? <Aviso tono="error">{t.comun.sinRed}</Aviso> : null}
        {resultado === "no-se-pudo" ? (
          <Aviso tono="aviso" titulo={t.comoAutorizar.noSePudoTitulo}>
            {explicacion ?? t.comoAutorizar.noSePudoDetalle}
          </Aviso>
        ) : null}

        <p className="ayuda-neutra">{t.comoAutorizar.pie}</p>
      </div>
    </Tarjeta>
  );
}
